import React, { useState } from "react";
import "./styles.css";
import { jsPDF } from "jspdf";
import { svg2pdf } from "svg2pdf.js";
import { QRCodeSVG } from "qrcode.react";

// ---------- Helpers de layout do PDF (escopo de módulo) ----------

// Quebra os segmentos em "palavras", onde cada palavra pode conter
// fragmentos com estilos diferentes (ex.: "Moretti," = bold + normal).
function buildWords(segments) {
  const words = [];
  let cur = [];
  for (const seg of segments) {
    if (!seg.t) continue;
    const parts = seg.t.split(" ");
    parts.forEach((part, j) => {
      if (j > 0) {
        if (cur.length) {
          words.push(cur);
          cur = [];
        }
      }
      if (part !== "") cur.push({ t: part, bold: !!seg.bold });
    });
  }
  if (cur.length) words.push(cur);
  return words;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Encaixa a imagem numa caixa preservando a proporção (object-fit: contain)
function drawFitted(doc, img, bx, by, bw, bh) {
  const ar = img.naturalWidth / img.naturalHeight;
  let w = bw;
  let h = bw / ar;
  if (h > bh) {
    h = bh;
    w = bh * ar;
  }
  const x = bx + (bw - w) / 2;
  const y = by + (bh - h) / 2;
  doc.addImage(img, "PNG", x, y, w, h);
  return { x, y, w, h };
}

// ---------- Cores ----------
const C_MAROON_DARK = [84, 0, 10]; // #54000a
const C_MAROON = [134, 50, 49]; // #863231
const C_BODY = [43, 43, 43]; // #2b2b2b
const C_GRAY = [85, 85, 85]; // #555
const C_SIGN = [51, 51, 51]; // #333

export default function CertificationModern({
  course,
  holder,
  courseUser,
  signatures,
  logos,
  leftTopics,
  rightTopics,
  workload,
  url,
  formatDate,
  isOwner,
}) {
  const presentSignatures = (signatures || []).filter(Boolean);
  const hasTopics =
    (leftTopics && leftTopics.length) || (rightTopics && rightTopics.length);

  // Estado para garantir que a imagem não use o cache incorreto inicial do navegador
  const [cacheBuster] = useState(new Date().getTime());

  async function printDocument() {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const W = doc.internal.pageSize.getWidth(); // 297
      const H = doc.internal.pageSize.getHeight(); // 210
      const cx = W / 2;

      // ---- Moldura (vetorial) ----
      const drawFrame = () => {
        doc.setDrawColor(...C_MAROON);
        doc.setLineWidth(0.3);
        doc.rect(10, 10, 277, 190); // linha fina externa
        doc.setLineWidth(0.7);
        doc.rect(13, 13, 271, 184); // borda principal

        doc.setDrawColor(...C_MAROON_DARK);
        doc.setLineWidth(1.2);
        const cl = 7; // comprimento dos cantos
        // cantos: TL, TR, BL, BR do retângulo interno (13,13)-(284,197)
        doc.line(13, 13, 13 + cl, 13);
        doc.line(13, 13, 13, 13 + cl);
        doc.line(284, 13, 284 - cl, 13);
        doc.line(284, 13, 284, 13 + cl);
        doc.line(13, 197, 13 + cl, 197);
        doc.line(13, 197, 13, 197 - cl);
        doc.line(284, 197, 284 - cl, 197);
        doc.line(284, 197, 284, 197 - cl);
      };

      // Texto centralizado com espaçamento entre letras (letter-spacing)
      const centeredSpaced = (text, y, charSpace) => {
        const w =
          doc.getTextWidth(text) + charSpace * Math.max(0, text.length - 1);
        doc.text(text, cx - w / 2, y, { charSpace });
      };

      // ===================== PÁGINA 1 =====================
      drawFrame();

      // Eyebrow
      doc.setFont("times", "normal");
      doc.setFontSize(11);
      doc.setTextColor(...C_MAROON);
      centeredSpaced("CENTRO ACADÊMICO EMÍLIO RIBAS", 30, 0.9);

      // Título
      doc.setFont("times", "bold");
      doc.setFontSize(34);
      doc.setTextColor(...C_MAROON_DARK);
      centeredSpaced("CERTIFICADO", 48, 2);

      // Divisor
      doc.setDrawColor(...C_MAROON);
      doc.setLineWidth(0.9);
      doc.line(cx - 13, 54, cx + 13, 54);

      // ---- Corpo (parágrafo justificado com negrito inline) ----
      const seg = [];
      seg.push({ t: "Certificamos que " });
      seg.push({ t: holder.name, bold: true });
      seg.push({ t: ", CPF " });
      seg.push({ t: holder.cpf, bold: true });
      seg.push({ t: ", participou do " });
      seg.push({ t: course.name, bold: true });
      seg.push({
        t: course.ending_date
          ? ` de ${formatDate(course.beginning_date)} até ${formatDate(course.ending_date)}`
          : ` em ${formatDate(course.beginning_date)}`,
      });
      if (courseUser && courseUser.user_quality) {
        seg.push({ t: `, na qualidade de ${courseUser.user_quality}` });
      }
      seg.push({ t: ", organizado pelo " });
      seg.push({ t: course.responsibleBody, bold: true });
      seg.push({
        t: ", vinculado ao Curso de Medicina do Centro Universitário Padre Albino (FAMECA/UNIFIPA)",
      });
      if (workload !== "") {
        seg.push({ t: `, com carga horária de ${workload} horas` });
      }
      seg.push({ t: "." });

      const BODY_SIZE = 13;
      const LH = 8; // altura de linha (mm)
      const leftX = 42;
      const maxW = W - leftX * 2; // 213

      doc.setFontSize(BODY_SIZE);

      const measureWord = (word) => {
        let w = 0;
        for (const frag of word) {
          doc.setFont("times", frag.bold ? "bold" : "normal");
          w += doc.getTextWidth(frag.t);
        }
        return w;
      };

      doc.setFont("times", "normal");
      const spaceW = doc.getTextWidth(" ");

      const words = buildWords(seg).map((w) => ({ w, width: measureWord(w) }));

      // Quebra em linhas
      const lines = [];
      let cur = [];
      let curW = 0;
      for (const m of words) {
        if (cur.length && curW + spaceW + m.width > maxW) {
          lines.push(cur);
          cur = [m];
          curW = m.width;
        } else {
          curW += (cur.length ? spaceW : 0) + m.width;
          cur.push(m);
        }
      }
      if (cur.length) lines.push(cur);

      // Renderiza cada linha (justificada, exceto a última)
      let y = 72;
      lines.forEach((line, li) => {
        const sumW = line.reduce((a, m) => a + m.width, 0);
        const gaps = line.length - 1;
        const isLast = li === lines.length - 1;
        let gap = spaceW;
        if (!isLast && gaps > 0) gap = (maxW - sumW) / gaps;

        let x = leftX;
        for (const m of line) {
          for (const frag of m.w) {
            doc.setFont("times", frag.bold ? "bold" : "normal");
            doc.setTextColor(...(frag.bold ? C_MAROON_DARK : C_BODY));
            doc.text(frag.t, x, y);
            x += doc.getTextWidth(frag.t);
          }
          x += gap;
        }
        y += LH;
      });

      // ---- Assinaturas ----
      if (presentSignatures.length) {
        const boxW = 46;
        const boxH = 18;
        const gap = 24;
        const total =
          presentSignatures.length * boxW +
          (presentSignatures.length - 1) * gap;
        let sx = (W - total) / 2;
        const boxTop = 138;

        for (const sig of presentSignatures) {
          try {
            const img = await loadImage(`${sig.image_path}?v=${cacheBuster}`);
            drawFitted(doc, img, sx, boxTop, boxW, boxH);
          } catch (e) {
            console.warn(
              "Assinatura não carregada (CORS?):",
              sig.image_path,
              e,
            );
          }
          // linha
          doc.setDrawColor(...C_SIGN);
          doc.setLineWidth(0.4);
          doc.line(sx + 3, boxTop + boxH + 2, sx + boxW - 3, boxTop + boxH + 2);
          // nome
          doc.setFont("times", "normal");
          doc.setFontSize(10);
          doc.setTextColor(...C_SIGN);
          doc.text(sig.name, sx + boxW / 2, boxTop + boxH + 7, {
            align: "center",
          });

          sx += boxW + gap;
        }
      }

      // ---- Rodapé: logos (esquerda) ----
      if (logos && logos.length) {
        let lx = 42;
        const lBoxW = 34;
        const lBoxH = 16;
        const lGap = 6;
        const lTop = 176;
        for (const logo of logos) {
          try {
            const img = await loadImage(`${logo.image_path}?v=${cacheBuster}`);
            drawFitted(doc, img, lx, lTop, lBoxW, lBoxH);
          } catch (e) {
            console.warn("Logo não carregada (CORS?):", logo.image_path, e);
          }
          lx += lBoxW + lGap;
        }
      }

      // ---- Rodapé: QR (reaproveita o <QRCodeSVG> já renderizado no DOM) ----
      const qrSize = 28;
      const qrX = 190;
      const qrY = 166;
      const qrSvg = document.querySelector("#divToPrintModern .qrCode svg");
      if (qrSvg) {
        await svg2pdf(qrSvg, doc, {
          x: qrX,
          y: qrY,
          width: qrSize,
          height: qrSize,
        });
      } else {
        console.warn("SVG do QR não encontrado no DOM.");
      }

      doc.setFont("times", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...C_GRAY);
      const vX = qrX + qrSize + 5;
      doc.text(
        "Emitido pelo Centro Acadêmico Emílio Ribas.",
        vX,
        qrY + qrSize / 2 - 1,
      );
      doc.text(
        "Verifique a autenticidade no QR Code.",
        vX,
        qrY + qrSize / 2 + 4,
      );

      // ===================== PÁGINA 2 (tópicos) =====================
      if (hasTopics) {
        doc.addPage();
        drawFrame();

        doc.setFont("times", "bold");
        doc.setFontSize(22);
        doc.setTextColor(...C_MAROON_DARK);
        centeredSpaced("Tópicos Abordados", 45, 0.5);

        doc.setDrawColor(...C_MAROON);
        doc.setLineWidth(0.9);
        doc.line(cx - 13, 52, cx + 13, 52);

        doc.setFont("courier", "normal");
        doc.setFontSize(11);
        doc.setTextColor(...C_BODY);

        const colTop = 70;
        const step = 8;
        (leftTopics || []).forEach((topic, i) => {
          doc.text(String(topic), 55, colTop + i * step);
        });
        (rightTopics || []).forEach((topic, i) => {
          doc.text(String(topic), 160, colTop + i * step);
        });
      }

      doc.save(`Certificado_${holder.name}.pdf`);
    } catch (error) {
      console.error("Falha ao gerar o PDF:", error);
      alert("Ocorreu um erro ao gerar o PDF. Tente novamente.");
    }
  }

  return (
    <div className="certificationModern">
      <div id="divToPrintModern">
        <div className="cmPage">
          <div className="cmFrame">
            <span className="cmCorner cmCornerTL" />
            <span className="cmCorner cmCornerTR" />
            <span className="cmCorner cmCornerBL" />
            <span className="cmCorner cmCornerBR" />

            <header className="cmHeader">
              <p className="cmEyebrow">Centro Acadêmico Emílio Ribas</p>
              <h1 className="cmTitle">Certificado</h1>
              <div className="cmDivider" />
            </header>

            <main className="cmBody">
              <p className="cmText">
                Certificamos que <strong>{holder.name}</strong>, CPF{" "}
                <strong>{holder.cpf}</strong>, participou do{" "}
                <strong>{course.name}</strong>
                {course.ending_date
                  ? ` de ${formatDate(course.beginning_date)} até ${formatDate(course.ending_date)}`
                  : ` em ${formatDate(course.beginning_date)}`}
                {courseUser && courseUser.user_quality
                  ? `, na qualidade de ${courseUser.user_quality}`
                  : ""}
                , organizado pelo <strong>{course.responsibleBody}</strong>,
                vinculado ao Curso de Medicina do Centro Universitário Padre
                Albino (FAMECA/UNIFIPA)
                {workload !== ""
                  ? `, com carga horária de ${workload} horas`
                  : ""}
                .
              </p>
            </main>

            {presentSignatures.length > 0 ? (
              <section className="cmSignatures">
                {presentSignatures.map((sig, i) => (
                  <div className="cmSignature" key={"cmSig" + i}>
                    <img
                      crossOrigin="anonymous"
                      className="signatureImg cmSignatureImg"
                      alt="signature"
                      src={`${sig.image_path}?v=${cacheBuster}`}
                    />
                    <div className="cmSignatureLine" />
                    <span className="cmSignatureName">{sig.name}</span>
                  </div>
                ))}
              </section>
            ) : null}

            <footer className="cmFooter">
              <div className="cmLogos">
                {(logos || []).map((logo) => (
                  <img
                    crossOrigin="anonymous"
                    className="cmLogo"
                    alt="logo"
                    src={`${logo.image_path}?v=${cacheBuster}`}
                    key={"cmLogo" + logo.id}
                  />
                ))}
              </div>
              <div className="cmVerify">
                <div className="qrCode">
                  <QRCodeSVG value={url} size={150} />
                </div>
                <div className="cmVerifyText">
                  <span>Emitido pelo Centro Acadêmico Emílio Ribas.</span>
                  <span>Verifique a autenticidade no QR Code.</span>
                </div>
              </div>
            </footer>
          </div>
        </div>

        {hasTopics ? (
          <div className="cmPage cmPageTopics">
            <div className="cmFrame">
              <h2 className="cmTopicsTitle">Tópicos Abordados</h2>
              <div className="cmDivider cmDividerCenter" />
              <div className="cmTopics">
                <div className="cmTopicsCol">
                  {(leftTopics || []).map((topic, i) => (
                    <span className="cmTopic" key={"cmLeft" + i}>
                      {topic}
                    </span>
                  ))}
                </div>
                <div className="cmTopicsCol">
                  {(rightTopics || []).map((topic, i) => (
                    <span className="cmTopic" key={"cmRight" + i}>
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {isOwner ? (
        <button
          id="printButtonModern"
          className="cmPrintButton"
          onClick={printDocument}
        >
          Download PDF
        </button>
      ) : null}
    </div>
  );
}
